import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import {
  DEFAULT_EDGE_ANIM,
  DEFAULT_NODE_THEME,
  LS_EDGE_ANIM,
  LS_NODE_THEME,
  isEdgeAnimId,
  isNodeThemeId,
  type EdgeAnimId,
  type NodeThemeId,
} from '@/features/studio/canvas/theme/registry'

interface CanvasThemeContextValue {
  nodeTheme: NodeThemeId
  edgeAnim: EdgeAnimId
  pickerOpen: boolean
  setNodeTheme: (id: NodeThemeId) => void
  setEdgeAnim: (id: EdgeAnimId) => void
  openPicker: () => void
  closePicker: () => void
  togglePicker: () => void
}

const CanvasThemeContext = createContext<CanvasThemeContextValue | null>(null)

function readStoredNodeTheme(): NodeThemeId {
  try {
    const v = localStorage.getItem(LS_NODE_THEME)
    if (v && isNodeThemeId(v)) return v
  } catch {
    /* ignore */
  }
  return DEFAULT_NODE_THEME
}

function readStoredEdgeAnim(): EdgeAnimId {
  try {
    const v = localStorage.getItem(LS_EDGE_ANIM)
    if (v && isEdgeAnimId(v)) return v
  } catch {
    /* ignore */
  }
  return DEFAULT_EDGE_ANIM
}

export function CanvasThemeProvider({ children }: { children: ReactNode }) {
  const [nodeTheme, setNodeThemeState] = useState<NodeThemeId>(readStoredNodeTheme)
  const [edgeAnim, setEdgeAnimState] = useState<EdgeAnimId>(readStoredEdgeAnim)
  const [pickerOpen, setPickerOpen] = useState(false)

  const setNodeTheme = useCallback((id: NodeThemeId) => {
    setNodeThemeState(id)
    try {
      localStorage.setItem(LS_NODE_THEME, id)
    } catch {
      /* ignore */
    }
  }, [])

  const setEdgeAnim = useCallback((id: EdgeAnimId) => {
    setEdgeAnimState(id)
    try {
      localStorage.setItem(LS_EDGE_ANIM, id)
    } catch {
      /* ignore */
    }
  }, [])

  const value = useMemo<CanvasThemeContextValue>(
    () => ({
      nodeTheme,
      edgeAnim,
      pickerOpen,
      setNodeTheme,
      setEdgeAnim,
      openPicker: () => setPickerOpen(true),
      closePicker: () => setPickerOpen(false),
      togglePicker: () => setPickerOpen((v) => !v),
    }),
    [nodeTheme, edgeAnim, pickerOpen, setNodeTheme, setEdgeAnim],
  )

  return <CanvasThemeContext.Provider value={value}>{children}</CanvasThemeContext.Provider>
}

export function useCanvasTheme(): CanvasThemeContextValue {
  const ctx = useContext(CanvasThemeContext)
  if (!ctx) throw new Error('useCanvasTheme must be used within CanvasThemeProvider')
  return ctx
}
